/**
 * The menus on their own (spec 08 §4), over the title scene (the pause menu over level 1), driven by
 * the keyboard and the mouse so the page is usable by hand:
 *
 *   ?scene=menu&screen=options       (title, options, controls, credits, changes, pause)
 *   &input=down.down.right           scripted frames before the first picture, for captures:
 *                                    up down left right select back p2, key-Space, click-320-200
 *
 * Arrows move, Enter / Space / X choose, Esc / Backspace go back, K is player 2's fire.
 * Owned by the MENUS stream from M3 on.
 */

import { level } from '../levels';
import { GameView } from '../render/game/GameView';
import { levelLook } from '../render/levelLook';
import { Palette } from '../render/palette';
import { SettingsStore } from '../settings';
import { HUD_H, HUD_W } from '../ui/hud/panels';
import { type MenuAction, type MenuColours, type MenuInput, type MenuScreen, Menus } from '../ui/menu';
import { chooseSize, createRenderer, exposeLook } from './devKit';
import { RELEASES, VERSION } from './version';

const SCREENS: readonly MenuScreen[] = ['title', 'options', 'controls', 'credits', 'changes', 'pause'];
const FRAME_MS = 1000 / 50;

const blank = (): MenuInput => ({
  up: false, down: false, left: false, right: false, select: false, back: false, p2Fire: false,
  key: null, pointer: null,
});

/** One scripted frame: `down`, `key-Space`, `click-320-200`. */
function scripted(token: string): MenuInput {
  const input = blank();
  const [kind = '', a = '', b = ''] = token.split('-');
  if (kind === 'key') input.key = a;
  else if (kind === 'click') input.pointer = { x: Number(a), y: Number(b), click: true };
  else if (kind === 'p2') input.p2Fire = true;
  else if (kind in input && kind !== 'key' && kind !== 'pointer') Object.assign(input, { [kind]: true });
  return input;
}

export function startMenuScene(canvas: HTMLCanvasElement, params: URLSearchParams): void {
  const first = SCREENS.find((s) => s === params.get('screen')) ?? 'title';
  const view = new GameView(createRenderer(canvas), chooseSize(params), level(1));
  view.showBackdrop(first === 'pause' ? null : 'title');
  // Its own options, not the game's saved ones: captures repeat exactly, even across a hot reload.
  const store = new SettingsStore(null);
  const menus = new Menus(store, RELEASES, VERSION);
  menus.open(first);
  const playing = first === 'pause';
  const hud = { level: 1, hiScore: 20000, players: [
    { playing, score: playing ? 12340 : 0, lives: playing ? 2 : 0, extend: [true, false, false, false, false, false] },
    { playing: false, score: 0, lives: 0, extend: [] },
  ] } as const;
  const p = new Palette(levelLook(level(1)));
  const colours: MenuColours = {
    p1: p.hex('bub.3'), p2: p.hex('bob.3'), text: p.hex('paper.0'), dim: p.hex('metal.1'),
    accent: p.hex('hudAccent.0'), panel: p.hex('ink.0'), shadow: p.hex('ink.0'), warn: p.hex('angry.2'),
  };

  // A stand-in for the session: every action lands back on a menu, so the page stays usable.
  const act = (a: MenuAction | null): void => {
    if (!a) return;
    console.info('menu action', a);
    menus.open(a.type === 'resume' ? 'pause' : 'title');
  };
  for (const token of (params.get('input') ?? '').split('.').filter(Boolean)) act(menus.update(scripted(token)));

  let pending = blank();
  window.addEventListener('keydown', (e) => {
    const keys = store.get().keys;
    if (e.code.startsWith('Arrow') || ['Space', 'Tab', 'Backspace', 'Enter'].includes(e.code)) e.preventDefault();
    const edge = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' }[e.code];
    if (edge) Object.assign(pending, { [edge]: true }); // browser key repeat drives held arrows
    if (e.repeat) return;
    pending.key ??= e.code;
    if (e.code === 'Enter' || keys[0].fire.includes(e.code)) pending.select = true;
    if (e.code === 'Escape' || e.code === 'Backspace') pending.back = true;
    if (keys[1].fire.includes(e.code)) pending.p2Fire = true;
  });
  const toHud = (e: PointerEvent): { x: number; y: number } => {
    const r = view.hud.canvas.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * HUD_W, y: ((e.clientY - r.top) / r.height) * HUD_H };
  };
  window.addEventListener('pointermove', (e) => {
    pending.pointer = { ...toHud(e), click: pending.pointer?.click ?? false };
  });
  window.addEventListener('pointerdown', (e) => { pending.pointer = { ...toHud(e), click: true }; });

  const draw = (t: number): void =>
    view.render(null, new Map(), 1, t, hud, [], (ctx) => menus.draw(ctx, colours));
  const start = performance.now();
  let last = start;
  let acc = 0;
  const frame = (now: number): void => {
    acc = Math.min(acc + now - last, 5 * FRAME_MS);
    last = now;
    while (acc >= FRAME_MS) {
      act(menus.update(pending));
      pending = blank();
      acc -= FRAME_MS;
    }
    draw((now - start) / 1000);
    requestAnimationFrame(frame);
  };
  draw(0);
  requestAnimationFrame(frame);
  exposeLook(view.pipeline, () => draw((performance.now() - start) / 1000), { menus }, view.hud.canvas);
}
